import React, { useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Stack,
  Button,
  Alert,
  Card,
  CardContent,
  Grid,
  Divider,
} from '@mui/material';
import apiClient from '../../api';

const COMMA_SEPARATED_HINT =
  'Comma-separated for multiple recipients (e.g. ops@company.com, manager@company.com)';

function prefillFormField(company, field) {
  const value = company[field];
  if (value != null && String(value).trim()) return value;
  return company.notificationEmailOther || '';
}

function mapCompanyToForm(company) {
  return {
    notificationEmailTimeOff: prefillFormField(company, 'notificationEmailTimeOff'),
    notificationEmailApparel: prefillFormField(company, 'notificationEmailApparel'),
    notificationEmailWeeklyChecklist: prefillFormField(
      company,
      'notificationEmailWeeklyChecklist'
    ),
    notificationEmailRecruitment: company.notificationEmailRecruitment || '',
    notificationEmailQuoteFlatbed: company.notificationEmailQuoteFlatbed || '',
    notificationEmailQuoteVan: company.notificationEmailQuoteVan || '',
    phone: company.phone || '',
    intakeTestingBanner: !!company.intakeTestingBanner,
  };
}

const initialForm = {
  notificationEmailTimeOff: '',
  notificationEmailApparel: '',
  notificationEmailWeeklyChecklist: '',
  notificationEmailRecruitment: '',
  notificationEmailQuoteFlatbed: '',
  notificationEmailQuoteVan: '',
  phone: '',
  intakeTestingBanner: false,
};

export default function CompanySettingsCard() {
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState(initialForm);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiClient.get('/users/company/info');
      const company = res.data.company || {};
      setForm(mapCompanyToForm(company));
    } catch (e) {
      setError('Failed to load company settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setLoading(true);
    setError('');
    setSaved(false);
    try {
      await apiClient.patch('/users/company/info', {
        notificationEmailTimeOff: form.notificationEmailTimeOff,
        notificationEmailApparel: form.notificationEmailApparel,
        notificationEmailWeeklyChecklist: form.notificationEmailWeeklyChecklist,
        notificationEmailRecruitment: form.notificationEmailRecruitment,
        notificationEmailQuoteFlatbed: form.notificationEmailQuoteFlatbed,
        notificationEmailQuoteVan: form.notificationEmailQuoteVan,
        phone: form.phone,
        intakeTestingBanner: form.intakeTestingBanner,
      });
      setSaved(true);
    } catch (e) {
      setError(e?.response?.data?.message || 'Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  const patch = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 2 }}>
      <Typography variant="h6" sx={{ mb: 0.5 }}>
        Company Settings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure who receives email notifications for employee forms, customer intake, and
        your company phone number.
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {saved && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Saved
        </Alert>
      )}

      <Stack spacing={2.5}>
        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Driver &amp; operations forms
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Notifications when employees submit internal forms. Each type can go to a
              different inbox.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Time off and referrals"
                placeholder="hr@mycompany.com"
                value={form.notificationEmailTimeOff}
                onChange={patch('notificationEmailTimeOff')}
                fullWidth
                disabled={loading}
                helperText="New time off submissions and driver referrals"
              />
              <TextField
                label="Apparel orders"
                placeholder="apparel@mycompany.com"
                value={form.notificationEmailApparel}
                onChange={patch('notificationEmailApparel')}
                fullWidth
                disabled={loading}
                helperText="New apparel order submissions"
              />
              <TextField
                label="Weekly checklists"
                placeholder="safety@mycompany.com"
                value={form.notificationEmailWeeklyChecklist}
                onChange={patch('notificationEmailWeeklyChecklist')}
                fullWidth
                disabled={loading}
                helperText="Submitted weekly driver checklists"
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 2 }}>
              {COMMA_SEPARATED_HINT}
            </Typography>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Customer intake (quotes &amp; hiring)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Public-facing intake from your website or recruiting flows.
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Recruitment"
                placeholder="recruitment@mycompany.com"
                value={form.notificationEmailRecruitment}
                onChange={patch('notificationEmailRecruitment')}
                fullWidth
                disabled={loading}
                helperText="Driver applications and resume submissions"
              />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Quotes — flatbed"
                    placeholder="quotes-flatbed@mycompany.com"
                    value={form.notificationEmailQuoteFlatbed}
                    onChange={patch('notificationEmailQuoteFlatbed')}
                    fullWidth
                    disabled={loading}
                    helperText="Flatbed quote requests"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Quotes — van"
                    placeholder="quotes-van@mycompany.com"
                    value={form.notificationEmailQuoteVan}
                    onChange={patch('notificationEmailQuoteVan')}
                    fullWidth
                    disabled={loading}
                    helperText="Van / dry-van quote requests"
                  />
                </Grid>
              </Grid>
              <Typography variant="caption" color="text.secondary">
                {COMMA_SEPARATED_HINT}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Card variant="outlined">
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Company details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={2}>
              <TextField
                label="Phone"
                value={form.phone}
                onChange={patch('phone')}
                fullWidth
                disabled={loading}
              />
              <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
                <Typography variant="body2">Show TEST banner in intake emails</Typography>
                <Button
                  variant={form.intakeTestingBanner ? 'contained' : 'outlined'}
                  color={form.intakeTestingBanner ? 'warning' : 'inherit'}
                  size="small"
                  onClick={() =>
                    setForm({ ...form, intakeTestingBanner: !form.intakeTestingBanner })
                  }
                  disabled={loading}
                >
                  {form.intakeTestingBanner ? 'On' : 'Off'}
                </Button>
              </Stack>
              <Typography variant="caption" color="text.secondary">
                When enabled, emails include a prominent red TEST banner for non-production
                submissions.
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        <Box>
          <Button variant="contained" onClick={save} disabled={loading}>
            Save
          </Button>
          <Button sx={{ ml: 1 }} onClick={load} disabled={loading}>
            Reset
          </Button>
        </Box>
      </Stack>
    </Paper>
  );
}
